import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, MailOpen, MousePointerClick, Send, AlertTriangle, ShieldAlert } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

/**
 * Per-broadcast engagement metrics. Reads from POST /api/newsletter/admin/analytics
 * which aggregates Resend webhook events stored in `newsletter_events`.
 *
 * Renders nothing if there are zero qualifying broadcasts.
 */
const BroadcastAnalytics = ({ password, loggedIn }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loggedIn) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/newsletter/admin/analytics`, { password, limit: 10 });
        if (!cancelled) setRows(data.broadcasts || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || "No se pudieron cargar las métricas");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loggedIn, password]);

  if (!loggedIn) return null;

  return (
    <div className="bg-white rounded-3xl border border-sand/50 mt-10 overflow-hidden" data-testid="broadcast-analytics">
      <div className="p-7 border-b border-sand/40 flex items-center gap-3 flex-wrap">
        <BarChart3 size={18} className="text-terracotta" />
        <h2 className="font-serif text-2xl text-charcoal">Métricas de envíos</h2>
        <span className="text-[11px] text-charcoal/55">
          Resend · tasas de apertura y clic
        </span>
      </div>

      <div className="p-7">
        {loading && (
          <p className="text-sm text-charcoal/55 italic" data-testid="analytics-loading">Cargando métricas…</p>
        )}
        {error && (
          <p className="text-sm text-terracotta" data-testid="analytics-error">{error}</p>
        )}
        {!loading && !error && rows && rows.length === 0 && (
          <div className="text-sm text-charcoal/60 leading-relaxed" data-testid="analytics-empty">
            <p>Aún no hay datos de apertura ni clic.</p>
            <p className="mt-2 text-[12.5px] text-charcoal/50">
              Configura el webhook de Resend en{" "}
              <a
                href="https://resend.com/webhooks"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-terracotta"
              >
                resend.com/webhooks
              </a>{" "}
              apuntando a <code className="text-terracotta">/api/webhooks/resend</code> y añade{" "}
              <code className="text-terracotta">RESEND_WEBHOOK_SECRET</code> en tu .env.
              Después envía una newsletter y las métricas aparecerán aquí.
            </p>
          </div>
        )}

        {!loading && rows && rows.length > 0 && (
          <ul className="divide-y divide-sand/40 -mx-1">
            {rows.map((b) => (
              <li key={b.id} className="px-1 py-5" data-testid={`analytics-row-${b.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <div className="min-w-0">
                    <p className="font-serif text-lg text-charcoal truncate">{b.subject}</p>
                    <p className="text-[12px] text-charcoal/55 mt-1">
                      {formatDate(b.created_at)} · {b.recipients} destinatarias
                    </p>
                  </div>
                  <div className="flex items-center gap-5 shrink-0">
                    <div className="text-right">
                      <p className="text-[10px] tracking-[0.25em] uppercase text-charcoal/50">Apertura</p>
                      <p className="font-serif text-2xl text-terracotta" data-testid={`open-rate-${b.id}`}>
                        {b.open_rate}%
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] tracking-[0.25em] uppercase text-charcoal/50">Clic</p>
                      <p className="font-serif text-2xl text-sage" data-testid={`click-rate-${b.id}`}>
                        {b.click_rate}%
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-[12.5px]">
                  <Metric icon={Send} label="Entregadas" value={b.delivered} color="text-charcoal/70" />
                  <Metric icon={MailOpen} label="Aperturas" value={b.opened} color="text-terracotta" />
                  <Metric icon={MousePointerClick} label="Clics" value={b.clicked} color="text-sage" />
                  <Metric icon={AlertTriangle} label="Rebotes" value={b.bounced} color="text-amber-600" />
                  <Metric icon={ShieldAlert} label="Spam" value={b.complained} color="text-red-500" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value, color }) => (
  <div className="flex items-center gap-2">
    <Icon size={13} className={color} />
    <span className="text-charcoal/55">{label}</span>
    <strong className="text-charcoal ml-auto">{value}</strong>
  </div>
);

export default BroadcastAnalytics;
